import { LazyLoader } from "../helpers/lazy-loader";
import LPEvent from "../classes/LPEvent";

/**
 * @typedef {object} MobileMenuItemData
 * @property {number} [section_id] - The section ID.
 * @property {boolean} [enabled] - Whether the section is enabled.
 * @property {string} name - The display name.
 * @property {string} slug - The URL slug.
 * @property {MobileMenuItemData[]} [menu_items] - Nested child menu items.
 * @property {boolean} [renderAsHeader] - Whether to render as a sub-header.
 * @property {boolean} [disableSubHeader] - Whether to hide the "Show all" sub-header.
 */

/**
 * Mobile navigation menu — manages slide-based page navigation
 * with nested menu sections and back/forward page transitions.
 */
class MobileMenu {
    /**
     * Create a MobileMenu instance.
     *
     * @param {string} parentSelector - CSS selector for the menu container element.
     * @param {MobileMenuItemData[]} sections - Array of menu section data objects from the server.
     */
    constructor(parentSelector, sections) {
        /** @type {string | null} */
        this._icon = null;
        /** @type {number} */
        this._currentPage = 1;
        this._settings = {
            maxPages: 3,
        };

        /** @type {{ menu: Element | null, slider: Element | null, header: Element | null, title: Element | null, backBtn: Element | null, closeBtn: Element | null, logo: Element | null }} */
        this.elements = {
            menu: null,
            slider: null,
            header: null,
            title: null,
            backBtn: null,
            closeBtn: null,
            logo: null,
        };

        /** @type {MobileMenuItem[]} */
        this._sections = [];

        this.bindElements(parentSelector);
        this.processSections(sections);

        this.elements.backBtn?.addEventListener("click", () =>
            this.navigateBack(),
        );
        this.elements.closeBtn?.addEventListener("click", () => this.hide());
    }

    /**
     * Cache references to key menu DOM elements.
     *
     * @param {string} parentSelector - CSS selector for the menu container element.
     * @returns {void}
     */
    bindElements(parentSelector) {
        this.elements = {
            menu: document.querySelector(parentSelector),
            slider: document.querySelector(`${parentSelector} .slides`),
            header: document.querySelector(
                `${parentSelector} .mobile-menu-header`,
            ),
            title: document.querySelector(
                `${parentSelector} .mobile-menu-header .header-title .title-text`,
            ),
            backBtn: document.querySelector(
                `${parentSelector} .mobile-menu-header .navigate-back`,
            ),
            closeBtn: document.querySelector(
                `${parentSelector} .mobile-menu-header .close-menu`,
            ),
            logo: document.querySelector(
                `${parentSelector} .mobile-menu-header .header-title .logo`,
            ),
        };

        this._icon =
            document.querySelector(`${parentSelector} .mobile-menu-icon`)
                ?.outerHTML ?? null;
    }

    /**
     * Build MobileMenuItem instances for each enabled section.
     *
     * @param {MobileMenuItemData[]} sections - Array of menu section data objects.
     * @returns {void}
     */
    processSections(sections) {
        this._sections = [];

        for (const section of sections) {
            if (section.enabled) {
                const existingElement =
                    this.elements.menu?.querySelector(
                        `div[data-sectionid="${section.section_id}"]`,
                    ) ?? null;
                const menuSection = new MobileMenuItem(
                    section,
                    existingElement,
                    this._icon,
                );
                menuSection.onItemClicked.bind(this.onItemClicked.bind(this));
                this._sections.push(menuSection);
            }
        }
    }

    /**
     * Handle a menu item click — navigate forward or redirect.
     *
     * @param {any} e - The event object.
     * @param {MobileMenuItem} item - The clicked menu item.
     * @returns {void}
     */
    onItemClicked(e, item) {
        if (item.hasMenuItems) {
            this.renderNextPage(item);
            this.navigateForward();
        } else {
            location.href = item.slug;
        }
    }

    /**
     * Navigate back one page if not already on the first page.
     *
     * @returns {void}
     */
    navigateBack() {
        if (this._currentPage > 1) {
            this.navigateToPage(this._currentPage - 1);
        }
    }

    /**
     * Navigate forward one page if not already on the last page.
     *
     * @returns {void}
     */
    navigateForward() {
        if (this._currentPage < this._settings.maxPages) {
            this.navigateToPage(this._currentPage + 1);
        }
    }

    /**
     * Navigate to a specific page number by updating CSS classes.
     *
     * @param {number} page - The target page number (1-based).
     * @returns {void}
     */
    navigateToPage(page) {
        this._currentPage = page;
        this.elements.slider?.classList.remove("page-2", "page-3");
        if (page > 1) {
            this.elements.slider?.classList.add(`page-${this._currentPage}`);
        }
        this.updateHeader();
    }

    /**
     * Show the mobile menu overlay.
     *
     * @returns {void}
     */
    show() {
        this.elements.menu?.classList.add("show");
        document.body.classList.add("hasOverlay", "menu-open");
    }

    /**
     * Hide the mobile menu overlay.
     *
     * @todo Neither `show()` nor `hide()` resets `_currentPage`, so closing the
     * menu two levels deep and reopening it drops the user back into that
     * sub-page instead of the section list.
     *
     * @returns {void}
     */
    hide() {
        document.body.classList.remove("hasOverlay", "menu-open");
        this.elements.menu?.classList.remove("show");
    }

    /**
     * Get the current slide element based on the active page number.
     *
     * @returns {Element | null} The slide element, or null if not found.
     */
    get currentSlide() {
        return (
            this.elements.slider?.querySelector(
                `.slide[data-page="${this._currentPage}"]`,
            ) ?? null
        );
    }

    /**
     * Render a menu item's children into the next slide.
     *
     * @todo Rendering and navigation disagree about menu depth. At the last
     * slide there is no next sibling, so this bails out entirely and
     * `navigateForward` then bails on `maxPages` — a 4th level tap is a dead
     * end with no error. Add a `.slide[data-page="4"]` and the content
     * renders while the track stays on `page-3`; only the missing slide
     * hides the mismatch.
     *
     * @param {MobileMenuItem} item - The menu item whose children to render.
     * @returns {void}
     */
    renderNextPage(item) {
        const slide = this.currentSlide?.nextElementSibling;
        if (!(slide instanceof HTMLElement)) return;

        slide.innerHTML = "";
        const subHeader = this.getSubHeader(item);
        if (subHeader) {
            slide.appendChild(subHeader);
        }
        for (let i = 0; i < item.menuItems.length; i++) {
            item.menuItems[i].appendTo(slide);
        }
        slide.dataset.itemTitle = item.name;
    }

    /**
     * Build a "Show all" sub-header link element for a menu item.
     *
     * @param {{ isSubheaderDisabled: boolean, slug: string, name: string }} param0 - Destructured item properties.
     * @returns {Element | null} The sub-header element, or null if disabled.
     */
    getSubHeader({ isSubheaderDisabled, slug, name }) {
        if (isSubheaderDisabled) {
            return null;
        }
        const elm = document.createElement("div");
        elm.className = "mobile-menu-item mobile-menu-showall";
        elm.innerHTML = `<a href="${slug}">${window.TEXT1146} ${name}</a>`;
        return elm;
    }

    /**
     * Update the header title and back button visibility based on the current page.
     *
     * @returns {void}
     */
    updateHeader() {
        const currentSlide = this.currentSlide;
        const headerText =
            (currentSlide instanceof HTMLElement
                ? currentSlide.dataset.itemTitle
                : null) || null;
        if (headerText) {
            this.elements.header?.classList.add("hide-logo");
            this.elements.logo?.classList.add("d-none");
            if (this.elements.title instanceof HTMLElement) {
                this.elements.title.textContent = headerText;
                this.elements.title.classList.remove("d-none");
            }
        } else {
            this.elements.header?.classList.remove("hide-logo");
            this.elements.logo?.classList.remove("d-none");
            if (this.elements.title instanceof HTMLElement) {
                this.elements.title.textContent = "";
                this.elements.title.classList.add("d-none");
            }
        }
        this.elements.backBtn?.classList.toggle(
            "d-none",
            this._currentPage < 2,
        );
    }
}

/**
 * A single item within the mobile menu — may contain nested sub-items.
 */
class MobileMenuItem {
    /**
     * Create a MobileMenuItem instance.
     *
     * @param {MobileMenuItemData} item - The menu item data from the server.
     * @param {Element | null} existingElement - An existing element to reuse, or null to create one.
     * @param {string | null} icon - The HTML string for the navigation arrow icon.
     */
    constructor(item, existingElement, icon) {
        if (existingElement === void 0) {
            existingElement = null;
        }
        this._item = item;
        this._onClickEvent = new LPEvent("__sectionClicked");
        this._icon = icon;

        /** @type {Element} */
        this.elm = document.createElement("div");

        /**
         * The currently bound click listener, tracked so `addClickListener`
         * can remove it before re-binding on a later re-render, rather than
         * stacking a fresh listener onto the same node every time.
         *
         * @type {((this: Element, event: Event) => void) | null}
         */
        this._clickListener = null;

        /** @type {MobileMenuItem[]} */
        this._items = [];
        this._childrenInitialized = false;

        if (existingElement) {
            this.setElement(existingElement);
            this.addClickListener();
        } else {
            if (this._item.renderAsHeader) {
                this.createHeaderElement();
            } else {
                this.createElement();
            }
        }
    }

    /**
     * Recursively build MobileMenuItem instances for nested menu items.
     *
     * @returns {void}
     */
    processMenuItems() {
        if (this._childrenInitialized || !this.hasMenuItems) {
            return;
        }

        this._childrenInitialized = true;
        this._items = new Array();

        for (
            let i = 0;
            i <
            /** @type {MobileMenuItemData[]} */ (this._item.menu_items).length;
            i++
        ) {
            const item = new MobileMenuItem(
                /** @type {MobileMenuItemData[]} */ (this._item.menu_items)[i],
                null,
                this._icon,
            );
            item.onItemClicked.bind(this.onSubItemClicked.bind(this));
            this._items.push(item);
        }
    }

    /**
     * Create the default menu item DOM element.
     *
     * @returns {void}
     */
    createElement() {
        const elm = document.createElement("div");
        elm.className = "mobile-menu-item";
        elm.innerHTML = `<span class="menu-item-text">${this._item.name}</span>`;
        if (this.hasMenuItems && this._icon) {
            elm.insertAdjacentHTML("beforeend", this._icon);
        }
        this.setElement(elm);
    }

    /**
     * Create a sub-header style menu item DOM element.
     *
     * @returns {void}
     */
    createHeaderElement() {
        const elm = document.createElement("div");
        elm.className = "mobile-menu-item mobile-menu-subheader";
        elm.innerHTML = `<span class="menu-item-text">${this._item.name}</span>`;
        this.setElement(elm);
    }

    /**
     * Set the element reference for this menu item.
     *
     * @param {Element} elm - The element to store.
     * @returns {void}
     */
    setElement(elm) {
        this.elm = elm;
    }

    /**
     * Handle a direct click on this menu item from the UI.
     *
     * @param {any} e - The event object.
     * @returns {void}
     */
    onClickFromUI(e) {
        this._onClickEvent.trigger(this);
    }

    /**
     * Handle a click on a nested sub-item — bubble the event up.
     *
     * @param {any} e - The event object.
     * @param {MobileMenuItem} item - The sub-item that was clicked.
     * @returns {void}
     */
    onSubItemClicked(e, item) {
        this._onClickEvent.trigger(item);
    }

    /**
     * Get the click event emitter for this item.
     *
     * @returns {import("../classes/LPEvent").default} The LPEvent instance.
     */
    get onItemClicked() {
        return this._onClickEvent;
    }

    /**
     * Whether this item has nested child menu items.
     *
     * @returns {boolean} True if the item has child menu items.
     */
    get hasMenuItems() {
        return (
            Array.isArray(this._item.menu_items) &&
            this._item.menu_items.length > 0
        );
    }

    /**
     * Get the nested MobileMenuItem instances.
     *
     * @returns {MobileMenuItem[]} The child menu items.
     */
    get menuItems() {
        this.processMenuItems();
        return this._items;
    }

    /**
     * Get the URL slug for this menu item.
     *
     * @returns {string} The item's slug.
     */
    get slug() {
        return this._item.slug;
    }

    /**
     * Get the display name for this menu item.
     *
     * @returns {string} The item's name.
     */
    get name() {
        return this._item.name;
    }

    /**
     * Whether the sub-header should be hidden for this item's children.
     *
     * @returns {boolean} True if the sub-header is disabled.
     */
    get isSubheaderDisabled() {
        return this._item.disableSubHeader || false;
    }

    /**
     * Append this item's element to a container and bind the click listener.
     *
     * @param {Element} container - The container to append to.
     * @returns {void}
     */
    appendTo(container) {
        this.addClickListener();
        container.appendChild(this.elm);
    }

    /**
     * Bind the click event listener to this item's element (unless it's a
     * header). Removes any previously bound listener first, so re-rendering
     * a cached item (see `MobileMenu.renderNextPage`) never stacks a second
     * listener onto the same node.
     *
     * @returns {void}
     */
    addClickListener() {
        if (this._item.renderAsHeader) return;

        if (this._clickListener) {
            this.elm.removeEventListener("click", this._clickListener);
        }
        this._clickListener = this.onClickFromUI.bind(this);
        this.elm.addEventListener("click", this._clickListener);
    }
}

const mobileMenuLoader = new LazyLoader(
    () => {
        window.mobileMenu = new MobileMenu(
            "#mainMenuMobileOverview",
            window.menuData || [],
        );
        // @todo Missing the `:not([data-toggle])` guard the click binding below
        // uses, so a burger that never got a listener is still marked ready.
        document
            .querySelector(".lux-header .burger-menu")
            ?.classList.add("ready");
    },
    {
        idle: true,
    },
);
document
    .querySelector(".lux-header .burger-menu:not([data-toggle])")
    ?.addEventListener("click", (event) => {
        event.preventDefault();
        mobileMenuLoader.run();
        window.mobileMenu?.show();
    });
